import type { MergeField, PaymentAmount, ProposalSection } from "./proposal";

export type AgreementStatus =
  | "DRAFT"
  | "IN_REVIEW"
  | "APPROVED"
  | "SENT"
  | "VIEWED"
  | "PARTIALLY_SIGNED"
  | "EXECUTED"
  | "DECLINED"
  | "EXPIRED"
  | "TERMINATED"
  | "SUPERSEDED";

export type AgreementType = "SERVICE" | "SUPPLY" | "RATE_CONTRACT" | "MSA" | "NDA";

export const AGREEMENT_TYPE_LABELS: Record<AgreementType, string> = {
  SERVICE: "Client service agreement",
  SUPPLY: "Supply agreement",
  RATE_CONTRACT: "Annual rate contract",
  MSA: "Master services agreement",
  NDA: "Non-disclosure agreement",
};

export interface AgreementTemplate {
  id: string;
  name: string;
  description?: string | null;
  agreement_type: AgreementType;
  is_default: boolean;
  /** Inactive templates are hidden from new agreements */
  status?: boolean;
  used_by?: number;
  defaults?: { title?: string; term_months?: number; auto_renew?: boolean; renewal_notice_days?: number } | null;
  sections?: ProposalSection[];
  merge_fields?: Array<{ key: string; label: string }>;
}

/** The accepted quote's lines and terms, frozen on the agreement */
export interface AgreementCommercials {
  quotation_id: string | null;
  quote_number: string | null;
  lines: Array<{ description: string; spec?: string | null; unit?: string | null; quantity: number; unit_price: number; line_total: number }>;
  subtotal: number;
  tax_total: number;
  grand_total: number;
  payment_terms: string | null;
}

/** A row of /Agreement/List */
export interface AgreementListRow {
  id: string;
  display_number: string;
  version: number;
  title: string;
  agreement_type: AgreementType;
  agreement_status: AgreementStatus;
  contract_value: string | number;
  effective_date: string | null;
  end_date: string | null;
  deal_id: string;
  created_on: string;
  submitted_at: string | null;
  sent_at: string | null;
  executed_at: string | null;
  /** BUILT in the CRM, or UPLOADED: the customer's own PDF */
  source?: "BUILT" | "UPLOADED";
  /** Set on an amendment: the signed agreement it changes */
  amends_agreement_id?: string | null;
  deal?: { id: string; name: string } | null;
  account?: { id: string; name: string } | null;
  owner?: { id: string; first_name: string; last_name?: string | null } | null;
}

interface PersonRef {
  id: string;
  name: string;
  email?: string | null;
}

export interface AgreementSigner {
  id: string;
  party: "CUSTOMER" | "COMPANY";
  sign_order: number;
  signer_name: string;
  signer_title: string | null;
  signer_email: string | null;
  signer_status: "PENDING" | "VIEWED" | "SIGNED" | "DECLINED";
  signed_at: string | null;
  /** The link in use for this signer (never the link itself) */
  link?: { channel: "EMAIL" | "LINK"; recipient: string | null; view_count: number; last_viewed_at: string | null } | null;
}

export interface AgreementHistoryRow {
  id: string;
  action: string;
  from_status: string | null;
  to_status: string | null;
  comment: string | null;
  created_on: string;
}

/** /Agreement/ById */
export interface AgreementRecord {
  id: string;
  agreement_number: string;
  version: number;
  display_number: string;
  deal_id: string;
  account_id: string | null;
  contact_id: string | null;
  owner_id: string | null;
  quotation_id: string | null;
  title: string;
  agreement_type: AgreementType;
  agreement_status: AgreementStatus;
  sections: ProposalSection[];
  commercial_snapshot: AgreementCommercials | null;
  contract_value: string | number;
  effective_date: string | null;
  end_date: string | null;
  auto_renew: boolean;
  renewal_notice_days: number;
  sent_at: string | null;
  executed_at: string | null;
  created_on: string;
  deal: { id: string; name: string; stage?: string; stage_key?: string; deal_status?: string };
  account: { id: string; name: string } | null;
  contact: { id: string; name: string; email?: string | null; job_title?: string | null } | null;
  owner: PersonRef | null;
  created_by: PersonRef | null;
  template: { id: string; name: string } | null;
  versions: Array<{ id: string; display_number: string; version: number; agreement_status: AgreementStatus }>;
  signers: AgreementSigner[];
  history: AgreementHistoryRow[];
  editable: boolean;
  can_new_version: boolean;
  can_delete: boolean;
  /** Approval before sending */
  requires_review: boolean;
  pending_reviewer: PersonRef | null;
  reviewed_by: PersonRef | null;
  review_comment: string | null;
  parent_agreement_id: string | null;
  can_terminate: boolean;
  source?: "BUILT" | "UPLOADED";
  file_name?: string | null;
  file_size?: number | null;
  file_sha256?: string | null;
  amends: { id: string; display_number: string; title: string } | null;
  amendments: Array<{ id: string; display_number: string; agreement_status: AgreementStatus; amendment_changes: { end_date?: string; contract_value?: number; summary?: string } | null; amendment_applied_at: string | null; created_on: string }>;
  amendment_changes: { end_date?: string; contract_value?: number; summary?: string } | null;
  amendment_applied_at: string | null;
  can_amend: boolean;
  /** Days until an agreement in force ends (negative once it has) */
  days_left: number | null;
  renewed_count: number;
  terminated_at: string | null;
  termination_reason: string | null;
  orders: Array<{ id: string; order_number: string; order_status: string; grand_total: string | number; order_date: string }>;
  can_submit: boolean;
  can_review: boolean;
  can_send: boolean;
  can_countersign: boolean;
  is_expired: boolean;
  merge_fields: MergeField[];
  unknown_merge_fields: string[];
  payment: PaymentAmount[] | null;
  unfilled_placeholders: number;
}

/** The agreement as its signers read it: /Agreement/Preview and the document in /Public/Agreement/:token */
export interface AgreementRender {
  id: string;
  display_number: string;
  version: number;
  title: string;
  status: AgreementStatus;
  agreement_type: AgreementType;
  effective_date: string | null;
  end_date: string | null;
  customer: { company: string; contact: string; job_title: string; email: string };
  company: { name: string; email: string; phone: string; website: string; address: string; gstin: string };
  sections: Array<{
    id: string;
    type: string;
    title: string;
    html: string;
    variant?: "full" | "compact" | "columns";
    facts?: Array<{ label: string; value: string; group?: string }>;
    numbered?: boolean;
    show_title?: boolean;
  }>;
  commercials: AgreementCommercials | null;
  /** UPLOADED: the content is the customer's own PDF (no clauses) */
  source?: "BUILT" | "UPLOADED";
  file?: { name: string; size: number; sha256: string } | null;
  /** The company letterhead it prints on, when one is in use for agreements (room at the top and bottom of page 1, in mm) */
  letterhead?: { id: string; version: number; top: number; bottom: number } | null;
  payment: PaymentAmount[] | null;
  signers: Array<{ party: "CUSTOMER" | "COMPANY"; name: string; title: string | null; status: string; signed_at: string | null }>;
}

export type SignState = "open" | "waiting" | "signed" | "executed" | "declined" | "terminated" | "expired" | "superseded";

/** GET /Public/Agreement/:token */
export interface PublicAgreement {
  state: SignState;
  display_number: string;
  title: string;
  company: { name: string; website: string; has_logo: boolean };
  contact_person: { name: string; email: string; phone: string } | null;
  internal: boolean;
  letterhead?: { top: number; bottom: number; version: number } | null;
  document: AgreementRender | null;
  signer: { name: string; title: string | null; email: string | null; signed_at: string | null } | null;
  otp_required?: boolean;
  consent_text?: string | null;
  decline_reason?: string | null;
}

/** GET /Agreement/Summary */
export interface AgreementSummary {
  in_force: { count: number; value: number };
  awaiting_signature: { count: number; value: number };
  waiting_review: number;
  ending_30: number;
  ending_60: number;
  ending_90: number;
  by_status: Record<string, { count: number; value: number }>;
}
